import React, { useEffect, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import { calculateFillRatio } from '../../../../utils/stowage/stowageOperation/stowageOperationUtils';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { useRotationStore } from 'src/context/RotationContext';
import { getLastBerthCallInPortCall } from 'src/utils/stowage/stowageRotationUtils';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { calculateVolume } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import Decimal from 'decimal.js';
import OdfjellPanel, { PANEL_CONSTANTS } from 'src/components/ui/odfjellPanel/OdfjellPanel';
import { DefaultPanelWrapper, ElementWrapper, InputFieldsWrapper, PanelContent } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { getCargoOperationWeight, getFormattedCargoNumber, isManualCargo } from 'src/utils/stowage/cargoOperationUtils';
import { getCargoContentForCondition, getTankContentForCondition } from 'src/utils/stowage/manifest/tankPlanConditionUtils';
import { ValidationItem } from 'src/types/ViewModel/ValidationItemType';
import { hasInvalidFields } from 'src/utils/aolFieldsHelper';
import CargoNumber from '../../common/cargoNumber';

type StowageVM = {
    stowedWeight: Decimal;
    stowedVolume: Decimal;
    nominatedWeight?: number;
    overrideFillRatio?: boolean;
    fillRatio?: Decimal;
    isShipFigureAdjusted?: boolean;
    shipFigure?: Decimal | null;
    notValidFieldsArray?: [];
    hasInvalidFields?: boolean;
    cargoContent?: CargoContent;
    stowageItem?: Stowage;
};

const initialVM: StowageVM = {
    stowedWeight: new Decimal(0),
    stowedVolume: new Decimal(0),
    overrideFillRatio: false,
    isShipFigureAdjusted: false,
    shipFigure: null,
    notValidFieldsArray: [],
    hasInvalidFields: false,
    stowageItem: null,
};

type Props = {
    tank: Tank;
};

const ShipFigurePanel: React.FC<Props> = (props) => {
    const { selectedTankPlanCondition, setIsShipFigurePanelOpen, activeStowagePlan } = useStowageStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const [stowageOperationVM, setStowageOperationVM] = useState<StowageVM>(initialVM);
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const [tank, setTank] = useState<Tank>(null);
    const { saveStowageData } = usePostStowageApi();

    useEffect(() => {
        if (props.tank) {
            setTank(props.tank);
        }

        if (!activeStowagePlan || !props.tank) {
            return;
        }
        const cargoContent = getCargoContent(props.tank);
        const stowageItem = cargoContent?.stowage;

        if (stowageItem) {
            const cargoTemperature = getCargoTemperature(activeStowagePlan, stowageItem.cargoOperation.cargo, props.tank);
            const stowedVolume = calculateVolume(stowageItem.weight, stowageItem.cargoOperation.cargo, cargoTemperature);
            const fillRatio = calculateFillRatio(stowedVolume, props.tank).times(new Decimal(100));
            const overrideFillRatio = fillRatio.gt(new Decimal(98));

            const stowageOperationVM: StowageVM = {
                stowedWeight: cargoContent.weight,
                stowedVolume: calculateVolume(cargoContent.weight, stowageItem.cargoOperation.cargo, cargoTemperature),
                nominatedWeight: getCargoOperationWeight(activeStowagePlan, stowageItem.cargoOperation.cargo, 'Loading', 'nominated'),
                fillRatio: fillRatio,
                overrideFillRatio: overrideFillRatio,
                shipFigure: stowageItem.shipFigure ? new Decimal(stowageItem.shipFigure) : null,
                isShipFigureAdjusted: stowageItem.isShipFigureAdjusted,
                cargoContent: cargoContent,
                stowageItem: stowageItem,
            };
            setStowageOperationVM({ ...stowageOperationVM });
        } else {
            setIsShipFigurePanelOpen(false);
        }
    }, []);

    const getCargoContent = (tank: Tank): CargoContent | null => {
        const berthCall = selectedStowageRotationBerthCall ?? getLastBerthCallInPortCall(selectedStowageRotationPortCall);
        if (!berthCall) return null;

        const tankContent = getTankContentForCondition(
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            selectedTankPlanCondition,
            tank
        );

        if (!tankContent?.cargoContents?.length) return null;

        const cargoContent = getCargoContentForCondition(
            activeStowagePlan,
            selectedStowageRotationPortCall,
            selectedStowageRotationBerthCall,
            selectedTankPlanCondition
        );

        if (cargoContent.length > 0) {
            const matchingContent = tankContent.cargoContents.find(
                (cc) => cc.cargoOperation?.cargoOperationGuid === cargoContent[0].cargoOperation.cargoOperationGuid
            );
            if (matchingContent) return matchingContent;
        }

        return tankContent.cargoContents[0];
    };

    const onCancel = () => {
        setIsShipFigurePanelOpen(false);
    };

    const onSave = async () => {
        if (!activeStowagePlan) {
            return;
        }

        if (stowageOperationVM.stowageItem) {
            stowageOperationVM.stowageItem.isShipFigureAdjusted = stowageOperationVM.isShipFigureAdjusted;
            stowageOperationVM.stowageItem.shipFigure = stowageOperationVM.shipFigure?.toNumber();

            const stowagePlanChanges: StowagePlanChange[] = [
                createStowagePlanChange(
                    activeStowagePlan.stowagePlanGuid,
                    stowageOperationVM.stowageItem.stowageGuid,
                    StowageChangeDataType.StowageItem,
                    {
                        isShipFigureAdjusted: stowageOperationVM.stowageItem.isShipFigureAdjusted,
                        shipFigure: getShipFigureValue(),
                    }
                ),
            ];

            await saveStowageData(activeStowagePlan, stowagePlanChanges);
        }

        setIsShipFigurePanelOpen(false);
    };

    const onShipFigureInputChange = (changeObject: InputChangeData) => {
        const value = changeObject.event.target.value;

        stowageOperationVM.hasInvalidFields = false;

        if (value === '') {
            stowageOperationVM.shipFigure = stowageOperationVM.stowedWeight;
            stowageOperationVM.isShipFigureAdjusted = false;
        } else {
            stowageOperationVM.shipFigure = new Decimal(value);
            if (
                stowageOperationVM.stowageItem.weight
                    .toDecimalPlaces(3, Decimal.ROUND_DOWN)
                    .eq(new Decimal(stowageOperationVM.shipFigure).toDecimalPlaces(3, Decimal.ROUND_DOWN))
            ) {
                stowageOperationVM.shipFigure = null;
                stowageOperationVM.isShipFigureAdjusted = false;
            }

            if (stowageOperationVM.shipFigure && stowageOperationVM.shipFigure.gt(new Decimal(0))) {
                stowageOperationVM.isShipFigureAdjusted = true;
            }

            if (stowageOperationVM.shipFigure && stowageOperationVM.shipFigure.gt(getMaxStowageWeight())) {
                stowageOperationVM.hasInvalidFields = true;
            }
        }

        hasInvalidFields(stowageOperationVM, 'shipFigure', changeObject.isValid);
        setStowageOperationVM({ ...stowageOperationVM });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const getMinStowageWeight = (): Decimal => {
        const _minWeight = Number(stowageOperationVM.stowedWeight) * 0.8;
        return new Decimal(roundTo3Decimals(_minWeight));
    };

    const getMaxStowageWeight = (): Decimal => {
        const _maxWeight = Number(stowageOperationVM.stowedWeight) * 1.2;
        return new Decimal(roundTo3Decimals(_maxWeight));
    };

    const getShipFigure = (): Decimal | null => {
        return stowageOperationVM.shipFigure && stowageOperationVM.shipFigure.gt(new Decimal(0))
            ? stowageOperationVM.shipFigure
            : stowageOperationVM.stowedWeight;
    };

    const getShipFigureDisplayValue = (): string => {
        const shipFigure = getShipFigure();
        return shipFigure !== null ? roundTo3Decimals(shipFigure.toNumber()).toString() : '';
    };

    const isShipFigureValueAdjusted = (): boolean => {
        return stowageOperationVM.isShipFigureAdjusted;
    };

    const isSameAsStowedWeight = stowageOperationVM.stowedWeight.eq(stowageOperationVM.shipFigure || new Decimal(0));

    const canSave = (): boolean => {
        return (
            !stowageOperationVM.hasInvalidFields &&
            stowageOperationVM.shipFigure &&
            !stowageOperationVM.shipFigure.greaterThan(stowageOperationVM.stowedWeight.toNumber() * 1.2) &&
            !stowageOperationVM.shipFigure.lessThan(stowageOperationVM.stowedWeight.toNumber() * 0.8)
        );
    };

    const getShipFigureValue = (): number | null => {
        if (!stowageOperationVM.stowageItem.shipFigure) return null;
        if (isSameAsStowedWeight) return null;
        if (!isSameAsStowedWeight) return stowageOperationVM.stowageItem.shipFigure;
    };

    const customValidations: ValidationItem[] = [
        {
            validationFunction: () =>
                stowageOperationVM.shipFigure &&
                !(stowageOperationVM.shipFigure.greaterThan(getMinStowageWeight()) && stowageOperationVM.shipFigure.lessThan(getMaxStowageWeight())),
            errorMessage:
                'Value must be within ±20% of the stowed quantity. Acceptable range: ' +
                getMinStowageWeight() +
                ' - ' +
                getMaxStowageWeight() +
                '.' +
                ' If your value is outside this range, please contact support.',
        },
    ];

    return (
        tank && (
            <OdfjellPanel
                headerTitle={'Ship figure (ROB)'}
                extHeaderTitle={getFormattedCargoNumber(
                    stowageOperationVM.cargoContent.cargo.voyageNumber,
                    stowageOperationVM.cargoContent.cargo.cargoNumber,
                    isManualCargo(stowageOperationVM.cargoContent.cargo)
                )}
                defaultPosition="center"
                panelWidth={PANEL_CONSTANTS.SMALL_WIDTH}
                onCancel={onCancel}
                onAction={onSave}
                isActionDisabled={!canSave()}
                showCancelButton={true}
            >
                <PanelContent>
                    <DefaultPanelWrapper>
                        <ElementWrapper>
                            <InputFieldsWrapper gridColWidths="60% 40%">
                                <CargoNumber withPadding includeColoredBackground cargo={stowageOperationVM.cargoContent.cargo} />
                                <span>{tank.odfjellTankName}</span>
                            </InputFieldsWrapper>
                        </ElementWrapper>
                    </DefaultPanelWrapper>
                    <DefaultPanelWrapper className="highlight-panel">
                        <ElementWrapper>
                            <span className={isShipFigureValueAdjusted() ? 'adjusted-input' : ''}>
                                <OdfjellInputComponent
                                    isReadOnly={false}
                                    type={'number'}
                                    description={'Ship figure (ROB)'}
                                    value={getShipFigureDisplayValue()}
                                    name={'shipFigure'}
                                    onChange={onShipFigureInputChange}
                                    dataObject={stowageOperationVM}
                                    customValidations={customValidations}
                                    leaveBlank={true}
                                    unit={'MT'}
                                    validationInTooltip={true}
                                    key={'OdfjellInputComponent_1_1'}
                                    maxDecimals={3}
                                />
                            </span>
                            {!stowageOperationVM.isShipFigureAdjusted}
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                description={'Content weight'}
                                value={roundTo3Decimals(stowageOperationVM.stowedWeight.toNumber())}
                                name={'stowedWeight'}
                                dataObject={stowageOperationVM}
                                leaveBlank={true}
                                unit={'MT'}
                                validationInTooltip={true}
                                key={'OdfjellInputComponent_1_2'}
                            />
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                description={'Content volume'}
                                value={roundTo3Decimals(stowageOperationVM.stowedVolume.toNumber())}
                                name={'stowedVolume'}
                                dataObject={stowageOperationVM}
                                leaveBlank={true}
                                unit={'m³'}
                                validationInTooltip={true}
                                key={'OdfjellInputComponent_1_3'}
                            />
                        </ElementWrapper>
                    </DefaultPanelWrapper>
                </PanelContent>
            </OdfjellPanel>
        )
    );
};

export default ShipFigurePanel;
